import type { ElementRef } from '../../../lib/teact/teact';
import { getActions } from '../../../global';

import type { ApiFormattedText, ApiMessageEntity } from '../../../api/types';
import type { ObserveFn } from '../../../hooks/useIntersectionObserver';
import type { TextPart, ThreadId } from '../../../types';
import type { TextFilter } from './renderText';
import { ApiMessageEntityTypes } from '../../../api/types';

import buildClassName from '../../../util/buildClassName';
import { copyTextToClipboard } from '../../../util/clipboard';
import { buildCustomEmojiHtmlFromEntity } from '../../middle/composer/helpers/customEmoji';
import renderText from './renderText';
import { resolveSharedAnimationProps } from './sharedAnimationProps';

// Bare filenames like "skill.md" or "report.pdf" should not be rendered as URL links,
// even when the server detects them as URLs (because .md, .sh, .ai are valid country TLDs).
const RE_BARE_FILENAME = /^[a-zA-Z0-9_.-]+\.(?:md|txt|csv|json|json5|pdf|png|jpe?g|gif|svg|webp|ts|tsx|js|jsx|py|go|rs|ya?ml|xml|html|css|scss|sh|sql|log|toml|cfg|ini|env|lock)$/i;

function isLikelyBareFilename(text: string): boolean {
  if (/^(ftp|https?):\/\//i.test(text) || /^www\./i.test(text)) return false;
  return RE_BARE_FILENAME.test(text);
}

function parseMermaidLanguage(language?: string) {
  if (!language) return undefined;

  const normalized = language.trim();
  if (normalized.toLowerCase() === 'mermaid') {
    return { language: 'mermaid', svgDataUrl: undefined };
  }

  for (const separator of ['|', '\0', '\\x00', '\\u0000']) {
    const index = normalized.indexOf(separator);
    if (index <= 0) continue;

    const prefix = normalized.slice(0, index).trim().toLowerCase();
    if (prefix !== 'mermaid') continue;

    return {
      language: 'mermaid',
      svgDataUrl: normalized.slice(index + separator.length) || undefined,
    };
  }

  return undefined;
}

import MentionLink from '../../middle/message/MentionLink';
import Blockquote from '../Blockquote';
import CodeBlock from '../code/CodeBlock';
import MarkdownTable from '../code/MarkdownTable';
import MermaidBlock from '../code/MermaidBlock';
import CustomEmoji from '../CustomEmoji';
import SafeLink from '../SafeLink';
import Spoiler from '../spoiler/Spoiler';

interface IOrganizedEntity {
  entity: ApiMessageEntity;
  organizedIndexes: Set<number>;
  nestedEntities: IOrganizedEntity[];
}
type RenderTextParams = Parameters<typeof renderText>[2];

const HQ_EMOJI_THRESHOLD = 64;

export function renderTextWithEntities({
  text,
  entities,
  allowMarkdownTablePromotion,
  highlight,
  emojiSize,
  shouldRenderAsHtml,
  containerId,
  asPreview,
  isProtected,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  withTranslucentThumbs,
  sharedCanvasRef,
  sharedCanvasHqRef,
  cacheBuster,
  forcePlayback,
  noCustomEmojiPlayback,
  isInSelectMode,
  chatId,
  messageId,
  threadId,
  maxTimestamp,
}: {
  text: string;
  entities?: ApiMessageEntity[];
  allowMarkdownTablePromotion?: boolean;
  highlight?: string;
  emojiSize?: number;
  shouldRenderAsHtml?: boolean;
  containerId?: string;
  asPreview?: boolean;
  isProtected?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  withTranslucentThumbs?: boolean;
  sharedCanvasRef?: ElementRef<HTMLCanvasElement>;
  sharedCanvasHqRef?: ElementRef<HTMLCanvasElement>;
  cacheBuster?: string;
  forcePlayback?: boolean;
  noCustomEmojiPlayback?: boolean;
  isInSelectMode?: boolean;
  chatId?: string;
  messageId?: number;
  threadId?: ThreadId;
  maxTimestamp?: number;
}) {
  const promotedEntities = !shouldRenderAsHtml && allowMarkdownTablePromotion
    ? promoteMarkdownTablesToEntities(text, entities)
    : entities;

  if (!promotedEntities?.length) {
    if (!shouldRenderAsHtml && allowMarkdownTablePromotion) {
      const emojiFilter = emojiSize && emojiSize > HQ_EMOJI_THRESHOLD ? 'hq_emoji' : 'emoji';
      const settledBlockMarkdown = renderSettledBlockMarkdown(text, { emojiFilter, highlight });
      if (settledBlockMarkdown) {
        return settledBlockMarkdown;
      }
    }

    return renderMessagePart({
      content: text,
      allowSimpleMarkdown: allowMarkdownTablePromotion,
      highlight,
      emojiSize,
      shouldRenderAsHtml,
      asPreview,
    });
  }

  const result: TextPart[] = [];
  let deleteLineBreakAfterPre = false;

  const organizedEntities = organizeEntities(promotedEntities);

  // Recursive function to render regular and nested entities
  function renderEntity(
    textPartStart: number,
    textPartEnd: number,
    organizedEntity: IOrganizedEntity,
    isLastEntity: boolean,
  ) {
    const renderResult: TextPart[] = [];
    const { entity, nestedEntities } = organizedEntity;
    const { offset, length, type } = entity;

    // Render text before the entity
    let textBefore = text.substring(textPartStart, offset);
    const textBeforeLength = textBefore.length;
    if (textBefore) {
      if (deleteLineBreakAfterPre && textBefore.length > 0 && textBefore[0] === '\n') {
        textBefore = textBefore.substr(1);
        deleteLineBreakAfterPre = false;
      }
      if (textBefore) {
        renderResult.push(...renderMessagePart({
          content: textBefore,
          allowSimpleMarkdown: allowMarkdownTablePromotion,
          highlight,
          emojiSize,
          shouldRenderAsHtml,
          asPreview,
        }));
      }
    }

    const entityStartIndex = textPartStart + textBeforeLength;
    const entityEndIndex = entityStartIndex + length;

    let entityContent: TextPart = text.substring(offset, offset + length);
    const nestedEntityContent: TextPart[] = [];

    if (deleteLineBreakAfterPre && entityContent.length > 0 && entityContent[0] === '\n') {
      entityContent = entityContent.substr(1);
      deleteLineBreakAfterPre = false;
    }

    if (type === ApiMessageEntityTypes.Pre) {
      deleteLineBreakAfterPre = true;
    }

    // Render nested entities, if any
    if (nestedEntities.length) {
      let nestedIndex = entityStartIndex;

      nestedEntities.forEach((nestedEntity, nestedEntityIndex) => {
        const {
          renderResult: nestedResult,
          entityEndIndex: nestedEntityEndIndex,
        } = renderEntity(
          nestedIndex,
          entityEndIndex,
          nestedEntity,
          nestedEntityIndex === nestedEntities.length - 1,
        );

        nestedEntityContent.push(...nestedResult);
        nestedIndex = nestedEntityEndIndex;
      });
    }

    // Render the entity itself
    const newEntity = shouldRenderAsHtml
      ? processEntityAsHtml(entity, entityContent, nestedEntityContent)
      : processEntity({
        entity,
        entityContent,
        nestedEntityContent,
        highlight,
        containerId,
        asPreview,
        isProtected,
        observeIntersectionForLoading,
        observeIntersectionForPlaying,
        withTranslucentThumbs,
        emojiSize,
        sharedCanvasRef,
        sharedCanvasHqRef,
        cacheBuster,
        forcePlayback,
        noCustomEmojiPlayback,
        isInSelectMode,
        chatId,
        messageId,
        threadId,
        maxTimestamp,
      });

    if (Array.isArray(newEntity)) {
      renderResult.push(...newEntity);
    } else {
      renderResult.push(newEntity);
    }

    // Render text after the entity, if it is the last entity in the text,
    // or the last nested entity inside of another entity
    if (isLastEntity && entityEndIndex < textPartEnd) {
      let textAfter = text.substring(entityEndIndex, textPartEnd);
      if (deleteLineBreakAfterPre && textAfter.length > 0 && textAfter[0] === '\n') {
        textAfter = textAfter.substring(1);
      }
      if (textAfter) {
        renderResult.push(...renderMessagePart({
          content: textAfter,
          allowSimpleMarkdown: allowMarkdownTablePromotion,
          highlight,
          emojiSize,
          shouldRenderAsHtml,
          asPreview,
        }));
      }
    }

    return {
      renderResult,
      entityEndIndex,
    };
  }

  // Process highest-level entities
  let index = 0;

  organizedEntities.forEach((entity, arrayIndex) => {
    const { renderResult, entityEndIndex } = renderEntity(
      index,
      text.length,
      entity,
      arrayIndex === organizedEntities.length - 1,
    );

    result.push(...renderResult);
    index = entityEndIndex;
  });

  return result;
}

export function getTextWithEntitiesAsHtml(formattedText?: ApiFormattedText) {
  const { text, entities } = formattedText || {};
  if (!text) {
    return '';
  }

  const result = renderTextWithEntities({
    text,
    entities,
    shouldRenderAsHtml: true,
  }) as string[];

  if (Array.isArray(result)) {
    return result.join('');
  }

  return result;
}

function renderMessagePart({
  content,
  allowSimpleMarkdown,
  highlight,
  focusedQuote,
  emojiSize,
  shouldRenderAsHtml,
  asPreview,
}: {
  content: TextPart | TextPart[];
  allowSimpleMarkdown?: boolean;
  highlight?: string;
  focusedQuote?: string;
  emojiSize?: number;
  shouldRenderAsHtml?: boolean;
  asPreview?: boolean;
}) {
  if (Array.isArray(content)) {
    const result: TextPart[] = [];

    content.forEach((c) => {
      result.push(...renderMessagePart({
        content: c,
        allowSimpleMarkdown,
        highlight,
        focusedQuote,
        emojiSize,
        shouldRenderAsHtml,
        asPreview,
      }));
    });

    return result;
  }

  if (shouldRenderAsHtml) {
    return renderText(content, ['escape_html', 'emoji_html', 'br_html']);
  }

  const emojiFilter = emojiSize && emojiSize > HQ_EMOJI_THRESHOLD ? 'hq_emoji' : 'emoji';

  if (typeof content === 'string' && allowSimpleMarkdown) {
    const settledBlockMarkdown = renderSettledBlockMarkdown(content, {
      emojiFilter,
      highlight,
    });
    if (settledBlockMarkdown) {
      return settledBlockMarkdown;
    }
  }

  const filters: TextFilter[] = [emojiFilter];
  const params: RenderTextParams = {};
  if (allowSimpleMarkdown) {
    filters.push('simple_markdown');
  }
  if (!asPreview) {
    filters.push('br');
  }

  if (highlight) {
    filters.push('highlight');
    params.highlight = highlight;
  }

  return renderText(content, filters, params);
}

function renderSettledBlockMarkdown(
  content: string,
  { emojiFilter, highlight }: { emojiFilter: TextFilter; highlight?: string },
): TextPart[] | undefined {
  const lines = content.split('\n');
  const hasBlockMarkdown = lines.some((line) => {
    const trimmed = line.trim();
    return /^###\s+\S/.test(trimmed) || /^-\s+\S/.test(trimmed) || /^---+$/.test(trimmed);
  });

  if (!hasBlockMarkdown) {
    return undefined;
  }

  const inlineFilters: TextFilter[] = [emojiFilter, 'simple_markdown'];
  const paragraphFilters: TextFilter[] = [...inlineFilters, 'br'];
  const inlineParams: RenderTextParams = highlight ? { highlight } : {};
  if (highlight) {
    inlineFilters.push('highlight');
    paragraphFilters.push('highlight');
  }

  const result: TextPart[] = [];
  let index = 0;
  let blockIndex = 0;

  const renderInline = (text: string) => renderText(text, inlineFilters, inlineParams);
  const renderParagraph = (text: string) => renderText(text, paragraphFilters, inlineParams);

  while (index < lines.length) {
    if (!lines[index].trim()) {
      index += 1;
      continue;
    }

    const headingMatch = lines[index].match(/^###\s+(.+)$/);
    if (headingMatch) {
      result.push(<h3 key={`md-h3-${blockIndex++}`}>{renderInline(headingMatch[1])}</h3>);
      index += 1;
      continue;
    }

    if (/^---+$/.test(lines[index].trim())) {
      result.push(<hr key={`md-hr-${blockIndex++}`} />);
      index += 1;
      continue;
    }

    if (/^-\s+\S/.test(lines[index].trim())) {
      const items: string[] = [];
      while (index < lines.length && /^-\s+\S/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^-\s+/, ''));
        index += 1;
      }
      result.push(
        <ul key={`md-ul-${blockIndex++}`}>
          {items.map((item, itemIndex) => <li key={`md-li-${blockIndex}-${itemIndex}`}>{renderInline(item)}</li>)}
        </ul>,
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      index < lines.length
      && lines[index].trim()
      && !/^###\s+\S/.test(lines[index].trim())
      && !/^-\s+\S/.test(lines[index].trim())
      && !/^---+$/.test(lines[index].trim())
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    if (paragraphLines.length) {
      result.push(<p key={`md-p-${blockIndex++}`}>{renderParagraph(paragraphLines.join('\n'))}</p>);
    }
  }

  return result;
}

export function insertTextEntities(entities: ApiMessageEntity[], newEntities: ApiMessageEntity[]) {
  return newEntities.reduce((acc, newEntity) => {
    return insertTextEntity(acc, newEntity);
  }, entities);
}

export function insertTextEntity(entities: ApiMessageEntity[], newEntity: ApiMessageEntity) {
  const resultEntities: ApiMessageEntity[] = [];

  const newEntityStart = newEntity.offset;
  const newEntityEnd = newEntity.offset + newEntity.length;

  for (const existingEntity of entities) {
    const existingEntityStart = existingEntity.offset;
    const existingEntityEnd = existingEntity.offset + existingEntity.length;
    // Push as is if edges do not overlap
    if (existingEntityEnd <= newEntityStart
      || existingEntityStart > newEntityEnd
      || (existingEntityStart > newEntityStart
        && existingEntityEnd < newEntityEnd)
      || (existingEntityStart === newEntityStart && existingEntityEnd === newEntityEnd)) {
      resultEntities.push(existingEntity);
      continue;
    }

    // If start edge overlaps
    if (existingEntityStart < newEntityStart && existingEntityEnd > newEntityStart) {
      // Split entity in two
      resultEntities.push({
        ...existingEntity,
        length: newEntityStart - existingEntityStart,
      });
      resultEntities.push({
        ...existingEntity,
        offset: newEntityStart,
        length: existingEntityEnd - newEntityStart,
      });
    }

    // If end edge overlaps
    if (existingEntityStart < newEntityEnd
      && existingEntityEnd > newEntityEnd) {
      // Split entity in two
      resultEntities.push({
        ...existingEntity,
        offset: newEntityEnd,
        length: existingEntityEnd - newEntityStart - newEntity.length,
      });
      resultEntities.push({
        ...existingEntity,
        length: newEntityEnd - existingEntityStart,
      });
    }
  }

  resultEntities.push(newEntity);
  // Sort entities by offset, longer entities first
  return resultEntities.sort((a, b) => a.offset - b.offset || b.length - a.length);
}

// Organize entities in a tree-like structure to better represent how the text will be displayed
function organizeEntities(entities: ApiMessageEntity[]) {
  const organizedEntityIndexes = new Set<number>();
  const organizedEntities: IOrganizedEntity[] = [];

  entities.forEach((entity, index) => {
    if (organizedEntityIndexes.has(index)) {
      return;
    }

    const organizedEntity = organizeEntity(entity, index, entities, organizedEntityIndexes);
    if (organizedEntity) {
      organizedEntity.organizedIndexes.forEach((organizedIndex) => {
        organizedEntityIndexes.add(organizedIndex);
      });

      organizedEntities.push(organizedEntity);
    }
  });

  return organizedEntities;
}

function organizeEntity(
  entity: ApiMessageEntity,
  index: number,
  entities: ApiMessageEntity[],
  organizedEntityIndexes: Set<number>,
): IOrganizedEntity | undefined {
  const { offset, length } = entity;
  const organizedIndexes = new Set([index]);

  if (organizedEntityIndexes.has(index)) {
    return undefined;
  }

  // Determine any nested entities inside current entity
  const nestedEntities: IOrganizedEntity[] = [];
  const parsedNestedEntities = entities
    .filter((e, i) => i > index && e.offset >= offset && e.offset < offset + length)
    .map((e) => organizeEntity(e, entities.indexOf(e), entities, organizedEntityIndexes))
    .filter(Boolean);

  parsedNestedEntities.forEach((parsedEntity) => {
    let isChanged = false;

    parsedEntity.organizedIndexes.forEach((organizedIndex) => {
      if (!isChanged && !organizedIndexes.has(organizedIndex)) {
        isChanged = true;
      }

      organizedIndexes.add(organizedIndex);
    });

    if (isChanged) {
      nestedEntities.push(parsedEntity);
    }
  });

  return {
    entity,
    organizedIndexes,
    nestedEntities,
  };
}

function processEntity({
  entity,
  entityContent,
  nestedEntityContent,
  highlight,
  containerId,
  asPreview,
  isProtected,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  withTranslucentThumbs,
  emojiSize,
  sharedCanvasRef,
  sharedCanvasHqRef,
  cacheBuster,
  forcePlayback,
  noCustomEmojiPlayback,
  isInSelectMode,
  chatId,
  messageId,
  threadId,
  maxTimestamp,
}: {
  entity: ApiMessageEntity;
  entityContent: TextPart;
  nestedEntityContent: TextPart[];
  highlight?: string;
  focusedQuote?: string;
  containerId?: string;
  asPreview?: boolean;
  isProtected?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  withTranslucentThumbs?: boolean;
  emojiSize?: number;
  sharedCanvasRef?: ElementRef<HTMLCanvasElement>;
  sharedCanvasHqRef?: ElementRef<HTMLCanvasElement>;
  cacheBuster?: string;
  forcePlayback?: boolean;
  noCustomEmojiPlayback?: boolean;
  isInSelectMode?: boolean;
  chatId?: string;
  messageId?: number;
  threadId?: ThreadId;
  maxTimestamp?: number;
}) {
  const entityText = typeof entityContent === 'string' && entityContent;
  const renderedContent = nestedEntityContent.length ? nestedEntityContent : entityContent;
  const {
    withSharedAnimation,
    sharedCanvasRef: resolvedSharedCanvasRef,
    sharedCanvasHqRef: resolvedSharedCanvasHqRef,
  } = resolveSharedAnimationProps(sharedCanvasRef, sharedCanvasHqRef);

  function renderNestedMessagePart() {
    return renderMessagePart({
      content: renderedContent,
      highlight,
      emojiSize,
      asPreview,
    });
  }

  if (!entityText) {
    return renderNestedMessagePart();
  }

  if (asPreview) {
    const text = renderNestedMessagePart();
    if (entity.type === ApiMessageEntityTypes.Spoiler) {
      return <Spoiler>{text}</Spoiler>;
    }

    if (entity.type === ApiMessageEntityTypes.CustomEmoji) {
      return (
        <CustomEmoji
          key={cacheBuster ? `${cacheBuster}-${entity.offset}` : undefined}
          documentId={entity.documentId}
          size={emojiSize}
          isSelectable
          withSharedAnimation
          sharedCanvasRef={resolvedSharedCanvasRef}
          sharedCanvasHqRef={resolvedSharedCanvasHqRef}
          observeIntersectionForLoading={observeIntersectionForLoading}
          observeIntersectionForPlaying={observeIntersectionForPlaying}
          withTranslucentThumb={withTranslucentThumbs}
          forceAlways={forcePlayback}
          noPlay={noCustomEmojiPlayback}
        />
      );
    }
    return text;
  }

  switch (entity.type) {
    case ApiMessageEntityTypes.Bold:
      return <strong data-entity-type={entity.type}>{renderNestedMessagePart()}</strong>;
    case ApiMessageEntityTypes.Blockquote:
      return (
        <Blockquote canBeCollapsible={entity.canCollapse} isToggleDisabled={isInSelectMode}>
          {renderNestedMessagePart()}
        </Blockquote>
      );
    case ApiMessageEntityTypes.BotCommand:
      return (
        <a
          onClick={handleBotCommandClick}
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    case ApiMessageEntityTypes.Hashtag: {
      const [tag, username] = entityContent.split('@');
      return (
        <a
          onClick={() => handleHashtagClick(tag, username)}
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    }
    case ApiMessageEntityTypes.Cashtag: {
      const [tag, username] = entityContent.split('@');
      return (
        <a
          onClick={() => handleHashtagClick(tag, username)}
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    }
    case ApiMessageEntityTypes.Code:
      return (
        <code
          className={buildClassName('text-entity-code', 'clickable')}
          onClick={handleCodeClick}
          role="textbox"
          tabIndex={0}
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </code>
      );
    case ApiMessageEntityTypes.Email:
      return (
        <a
          href={`mailto:${entityText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    case ApiMessageEntityTypes.Italic:
      return <em data-entity-type={entity.type}>{renderNestedMessagePart()}</em>;
    case ApiMessageEntityTypes.MentionName:
      return (
        <MentionLink userId={entity.userId}>
          {renderNestedMessagePart()}
        </MentionLink>
      );
    case ApiMessageEntityTypes.Mention:
      return (
        <MentionLink username={entityText}>
          {renderNestedMessagePart()}
        </MentionLink>
      );
    case ApiMessageEntityTypes.Phone:
      return (
        <a
          href={`tel:${entityText}`}
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    case ApiMessageEntityTypes.Pre:
      {
        const mermaidPayload = parseMermaidLanguage(entity.language);
        if (mermaidPayload) {
          return (
            <MermaidBlock
              text={entityText}
              svgDataUrl={entity.url || mermaidPayload.svgDataUrl}
              noCopy={isProtected}
            />
          );
        }
      }
      if (entity.language === 'markdown-table' || (!entity.language && looksLikeMarkdownTable(entityText))) {
        return <MarkdownTable text={normalizeTableText(entityText)} />;
      }
      return <CodeBlock text={entityText} language={entity.language} noCopy={isProtected} />;
    case ApiMessageEntityTypes.Strike:
      return <del data-entity-type={entity.type}>{renderNestedMessagePart()}</del>;
    case ApiMessageEntityTypes.TextUrl:
    case ApiMessageEntityTypes.Url: {
      // Server detects bare filenames like "report.md" as URLs because .md is Moldova's TLD.
      // Render them as plain text instead of clickable links.
      if (entity.type === ApiMessageEntityTypes.Url && isLikelyBareFilename(entityText)) {
        return renderNestedMessagePart();
      }
      return (
        <SafeLink
          url={getLinkUrl(entityText, entity)}
          text={entityText}
          chatId={chatId}
          messageId={messageId}
          threadId={threadId}
          entityType={entity.type}
        >
          {renderNestedMessagePart()}
        </SafeLink>
      );
    }
    case ApiMessageEntityTypes.Underline:
      return <ins data-entity-type={entity.type}>{renderNestedMessagePart()}</ins>;
    case ApiMessageEntityTypes.Timestamp:
      if (!chatId || !messageId || !maxTimestamp || entity.timestamp > maxTimestamp) {
        return renderNestedMessagePart();
      }

      return (
        <a
          onClick={() => handleTimecodeClick(chatId, messageId, threadId, entity.timestamp)}
          className="text-entity-link"
          dir="auto"
          data-entity-type={entity.type}
        >
          {renderNestedMessagePart()}
        </a>
      );
    case ApiMessageEntityTypes.Spoiler:
      return <Spoiler containerId={containerId}>{renderNestedMessagePart()}</Spoiler>;
    case ApiMessageEntityTypes.CustomEmoji:
      return (
        <CustomEmoji
          key={cacheBuster ? `${cacheBuster}-${entity.offset}` : undefined}
          documentId={entity.documentId}
          size={emojiSize}
          isSelectable
          withSharedAnimation
          sharedCanvasRef={resolvedSharedCanvasRef}
          sharedCanvasHqRef={resolvedSharedCanvasHqRef}
          observeIntersectionForLoading={observeIntersectionForLoading}
          observeIntersectionForPlaying={observeIntersectionForPlaying}
          withTranslucentThumb={withTranslucentThumbs}
          forceAlways={forcePlayback}
          noPlay={noCustomEmojiPlayback}
        />
      );
    case ApiMessageEntityTypes.QuoteFocus:
      return (
        <span className="matching-text-highlight is-quote">{renderNestedMessagePart()}</span>
      );
    default:
      return renderNestedMessagePart();
  }
}

function processEntityAsHtml(
  entity: ApiMessageEntity,
  entityContent: TextPart,
  nestedEntityContent: TextPart[],
) {
  const rawEntityText = typeof entityContent === 'string' ? entityContent : undefined;

  // Prevent adding newlines when editing
  const content = entity.type === ApiMessageEntityTypes.Pre ? (entityContent as string).trimEnd() : entityContent;

  const renderedContent = nestedEntityContent.length
    ? (nestedEntityContent as string[]).join('')
    : (renderText(content, ['escape_html', 'emoji_html', 'br_html']) as string[]).join('');

  if (!rawEntityText) {
    return renderedContent;
  }

  switch (entity.type) {
    case ApiMessageEntityTypes.Bold:
      return `<b>${renderedContent}</b>`;
    case ApiMessageEntityTypes.Italic:
      return `<i>${renderedContent}</i>`;
    case ApiMessageEntityTypes.Underline:
      return `<u>${renderedContent}</u>`;
    case ApiMessageEntityTypes.Code:
      return `<code class="text-entity-code">${renderedContent}</code>`;
    case ApiMessageEntityTypes.Pre:
      // eslint-disable-next-line @stylistic/max-len
      return `\`\`\`${renderText(entity.language || '', ['escape_html'])[0] as string}<br/>${renderedContent}<br/>\`\`\`<br/>`;
    case ApiMessageEntityTypes.Strike:
      return `<del>${renderedContent}</del>`;
    case ApiMessageEntityTypes.MentionName:
      return `<a
        class="text-entity-link"
        data-entity-type="${ApiMessageEntityTypes.MentionName}"
        data-user-id="${entity.userId}"
        contenteditable="false"
        dir="auto"
      >${renderedContent}</a>`;
    case ApiMessageEntityTypes.Url:
    case ApiMessageEntityTypes.TextUrl: {
      if (entity.type === ApiMessageEntityTypes.Url && rawEntityText && isLikelyBareFilename(rawEntityText)) {
        return renderedContent;
      }
      return `<a
        class="text-entity-link"
        href=${getLinkUrl(rawEntityText, entity)}
        data-entity-type="${entity.type}"
        dir="auto"
      >${renderedContent}</a>`;
    }
    case ApiMessageEntityTypes.Spoiler:
      return `<span
        class="spoiler"
        data-entity-type="${ApiMessageEntityTypes.Spoiler}"
        >${renderedContent}</span>`;
    case ApiMessageEntityTypes.CustomEmoji:
      return buildCustomEmojiHtmlFromEntity(rawEntityText, entity);
    case ApiMessageEntityTypes.Blockquote:
      return `<blockquote
        class="blockquote"
        data-entity-type="${ApiMessageEntityTypes.Blockquote}"
        >${renderedContent}</blockquote>`;
    default:
      return renderedContent;
  }
}

function getLinkUrl(entityContent: string, entity: ApiMessageEntity) {
  const { type } = entity;
  return type === ApiMessageEntityTypes.TextUrl && entity.url ? entity.url : entityContent;
}

function handleBotCommandClick(e: React.MouseEvent<HTMLAnchorElement>) {
  getActions().sendBotCommand({ command: e.currentTarget.innerText });
}

function handleHashtagClick(hashtag?: string, username?: string) {
  if (!hashtag) return;
  if (username) {
    getActions().openChatByUsername({
      username,
      onChatChanged: {
        action: 'searchHashtag',
        payload: { hashtag },
      },
    });
    return;
  }
  getActions().searchHashtag({ hashtag });
}

function handleCodeClick(e: React.MouseEvent<HTMLElement>) {
  copyTextToClipboard(e.currentTarget.innerText);
  getActions().showNotification({
    message: {
      key: 'TextCopied',
    },
  });
}

function handleTimecodeClick(
  chatId: string, messageId: number, threadId: ThreadId | undefined, timestamp: number,
) {
  getActions().openMediaFromTimestamp({
    chatId, messageId, threadId, timestamp,
  });
}

/**
 * Merge broken table header rows: if a line looks like a table row (starts/ends with |)
 * and the next line doesn't start with | but ends with |, merge them.
 */
function normalizeTableText(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const prev = result.length > 0 ? result[result.length - 1].trim() : '';
    if (prev && /^\|.+\|$/.test(prev) && !trimmed.startsWith('|') && trimmed.includes('|') && trimmed.endsWith('|')) {
      result[result.length - 1] = result[result.length - 1].trimEnd() + ' ' + trimmed;
    } else {
      result.push(lines[i]);
    }
  }
  return result.join('\n');
}

function looksLikeMarkdownTable(text: string): boolean {
  const normalized = normalizeTableText(text);
  const lines = normalized.trim().split('\n');
  if (lines.length < 2) return false;
  // Header and separator must start/end with pipe
  if (!/^\|.+\|$/.test(lines[0].trim())) return false;
  if (!/^\|.+\|$/.test(lines[1].trim())) return false;
  // Separator row must contain dashes
  const sepCells = lines[1].replace(/^\|/, '').replace(/\|$/, '').split('|');
  return sepCells.every((c) => /^\s*:?-+:?\s*$/.test(c));
}

function promoteMarkdownTablesToEntities(text: string, entities?: ApiMessageEntity[]) {
  const promotedEntities = collectPromotableMarkdownTableEntities(text, entities);
  if (!promotedEntities.length) {
    return entities;
  }

  return [...(entities || []), ...promotedEntities].sort((a, b) => a.offset - b.offset || b.length - a.length);
}

function collectPromotableMarkdownTableEntities(text: string, entities?: ApiMessageEntity[]) {
  const lines = text.split('\n');
  const lineOffsets: number[] = [];
  let offset = 0;

  lines.forEach((line) => {
    lineOffsets.push(offset);
    offset += line.length + 1;
  });

  const promotedEntities: ApiMessageEntity[] = [];

  for (let i = 0; i < lines.length - 1; i++) {
    if (!isMarkdownTableRow(lines[i]) || !isMarkdownTableSeparatorRow(lines[i + 1])) {
      continue;
    }

    let endLine = i + 2;
    while (endLine < lines.length) {
      const trimmedLine = lines[endLine].trim();
      if (!trimmedLine) {
        let nextNonEmptyLine = endLine + 1;
        while (nextNonEmptyLine < lines.length && !lines[nextNonEmptyLine].trim()) {
          nextNonEmptyLine++;
        }
        if (
          nextNonEmptyLine < lines.length
          && (isMarkdownTableRow(lines[nextNonEmptyLine]) || isMarkdownTableSeparatorRow(lines[nextNonEmptyLine]))
        ) {
          endLine = nextNonEmptyLine;
          continue;
        }
        break;
      }

      if (!isMarkdownTableRow(lines[endLine]) && !isMarkdownTableSeparatorRow(lines[endLine])) {
        break;
      }

      endLine++;
    }

    const tableStart = lineOffsets[i];
    const tableEnd = lineOffsets[endLine - 1] + lines[endLine - 1].length;

    if (!hasEntityOverlap(tableStart, tableEnd, entities)) {
      promotedEntities.push({
        offset: tableStart,
        length: tableEnd - tableStart,
        type: ApiMessageEntityTypes.Pre,
        language: 'markdown-table',
      });
    }

    i = endLine - 1;
  }

  return promotedEntities;
}

function hasEntityOverlap(start: number, end: number, entities?: ApiMessageEntity[]) {
  return entities?.some((entity) => {
    if (isMarkdownTableCompatibleEntity(entity)) {
      return false;
    }

    const entityStart = entity.offset;
    const entityEnd = entity.offset + entity.length;

    return entityStart < end && entityEnd > start;
  }) || false;
}

function isMarkdownTableCompatibleEntity(entity: ApiMessageEntity) {
  return entity.type === ApiMessageEntityTypes.Url
    || entity.type === ApiMessageEntityTypes.TextUrl
    || entity.type === ApiMessageEntityTypes.EmailAddress;
}

function isMarkdownTableRow(line: string) {
  return /^\|.+\|$/.test(line.trim());
}

function isMarkdownTableSeparatorRow(line: string) {
  const trimmed = line.trim();
  if (!isMarkdownTableRow(trimmed)) {
    return false;
  }

  const separatorCells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|');
  return separatorCells.every((cell) => /^\s*:?-+:?\s*$/.test(cell));
}
